import * as React from 'react'

import {
  getContentRoutePathname,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import { Post } from '@thg-commerce/enterprise-network'
import {
  ContentMasthead,
  ContentMastheadProps,
  ContentMastheadType,
} from '@thg-commerce/gravity-elements'

interface ArticleHeaderRendererProps {
  post: Post
  isAmp: boolean
  hasWebP: boolean
}

export const constructImageUrl = (
  headerType: ContentMastheadType,
  fullImageUrl: string | undefined,
  hasWebP: boolean,
) => {
  if (fullImageUrl) {
    const imageRoot = 'https://static.thcdn.com/images'

    const fullImagePath = fullImageUrl.replace(
      /^.+blogscdn.thehut.net\/(?=app|wp-content)/g,
      '',
    )
    const imageType = hasWebP ? 'webp' : 'original'
    return {
      type: hasWebP ? 'image/webp' : 'image/jpeg',
      imageUris:
        headerType === ContentMastheadType.HERO
          ? [
              `${imageRoot}/xsmall/${imageType}/${fullImagePath} 300w`,
              `${imageRoot}/small/${imageType}/${fullImagePath} 600w`,
              `${imageRoot}/medium/${imageType}/${fullImagePath} 1200w`,
              `${imageRoot}/large/${imageType}/${fullImagePath} 1500w`,
              `${imageRoot}/xlarge/${imageType}/${fullImagePath} 2000w`,
            ]
          : [
              `${imageRoot}/xsmall/${imageType}/${fullImagePath} 300w`,
              `${imageRoot}/small/${imageType}/${fullImagePath} 600w`,
              `${imageRoot}/medium/${imageType}/${fullImagePath} 2400w`,
              `${imageRoot}/large/${imageType}/${fullImagePath} 3000w`,
              `${imageRoot}/xlarge/${imageType}/${fullImagePath} 4000w`,
            ],
    }
  }

  return null
}

export const ArticleHeaderRenderer = ({
  post,
  isAmp,
  hasWebP,
}: ArticleHeaderRendererProps) => {
  if (!post.titleDecoded) {
    return null
  }

  const { contentRoutePrefix } = useSiteDefinition()

  let headerType =
    post.postSettings?.headerType &&
    post.postSettings.headerType.toUpperCase() in ContentMastheadType
      ? ContentMastheadType[post.postSettings?.headerType?.toUpperCase()]
      : ContentMastheadType.TEXTONLY

  const fullImageUrl =
    headerType === ContentMastheadType.INSET
      ? post.featuredImage?.mediaItemUrl || undefined
      : post.postSettings?.headerHeroImage?.mediaItemUrl || undefined

  const image = constructImageUrl(headerType, fullImageUrl, hasWebP)

  if (
    image === null ||
    ([ContentMastheadType.HERO, ContentMastheadType.INSET].includes(
      headerType,
    ) &&
      image === null)
  ) {
    headerType = ContentMastheadType.TEXTONLY
  }

  const categoryNode = post?.categories?.nodes?.[0] || null
  const category =
    categoryNode && categoryNode.name && categoryNode.slug
      ? {
          name: categoryNode.name,
          url: getContentRoutePathname(contentRoutePrefix, categoryNode.slug),
        }
      : undefined

  const articleHeaderProps: ContentMastheadProps = {
    isAmp,
    category,
    image,
    fallbackImageUrl: fullImageUrl,
    type: headerType,
    title: post.titleDecoded,
  }

  return <ContentMasthead {...articleHeaderProps} />
}
